/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable no-unused-vars,@typescript-eslint/no-unused-vars,@typescript-eslint/dot-notation,dot-notation */
import { DragEventDelegate, DragInfo } from './interface/drag-event-delegate';
import throttle from 'lodash/throttle';
import ClearanceUtils from '@src/slide/domain/ui-model/utils/clearance-utils';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';

const SCROLL_AREA = 100;
const DEFAULT_CLEARANCE_HEIGHT = 10; // 间隙高度，大约为10

type THeight = number;
type TClearance = number;
type TSectionIndex = number;

/**
 * 处理拖拽事件，并通知组件更新UI
 */
export default class DragEventHandler {
    public static readonly MouseDragTimeout = 300;

    private dragDelegate: DragEventDelegate;

    private mouseDragTimeout;
    private unfoldSectionTimeoutContext: [TSectionIndex, ReturnType<typeof setTimeout>] | undefined;
    private listRect: DOMRect;
    private mouseOffset;
    private dragPos: number[];
    private oldScrollTop: number;
    private savedDragInfo: DragInfo;

    public constructor(dragDelegate: DragEventDelegate) {
        this.dragDelegate = dragDelegate;
    }

    /**
     * 处理单个item要拖拽的部分的mouseDown事件
     * @param event
     * @param listIndex 当前点击item索引
     * @param cell
     */
    public dragMouseDown(event, listIndex: number, cell: any, clearanceIndex: number): void {
        if (this.onPrepareMouseDown(event)) {
            return;
        }
        const { pageX, pageY } = event;
        const cellDom = event.currentTarget;
        document.addEventListener('mouseup', this.handleMouseUp); // frame监听

        // Only do drag if we hold the mouse down for a bit
        this.mouseDragTimeout = setTimeout(() => {
            this.mouseDragTimeout = null;

            this.initDragInfo(cellDom, pageX, pageY);  // 初始化初始拖拽位置信息
            const draggingArray = this.getDraggingArray(listIndex, cell);  // 保存拖拽列表

            const originalDragItem = this.dataList[listIndex];
            const dragInfo = {
                originalDragItem,
                originalDragIndex: clearanceIndex,
                currentDragIndex: clearanceIndex,
                outside: false,
                dragPos: this.dragPos,
                draggingArray,
            };
            this.dragDelegate.onDragStart(dragInfo);
            this.savedDragInfo = { ...dragInfo };
            // 主页面开始监听
            this.registerEventListeners();
        }, DragEventHandler.MouseDragTimeout);
    }

    public onSectionTitleDragMouseDown(
        event: MouseEvent,
        sectionIndex: number,
        clearanceIndex: number,
        toggleDraggingSection: (isDraggingSection: boolean) => void,
    ): void {
        if (this.onPrepareMouseDown(event)) {
            return;
        }
        const { pageX, pageY } = event;
        const sectionDom = event.target;
        this.savedDragInfo = { ...this.savedDragInfo, toggleDraggingSection, originalSectionIndex: sectionIndex };
        document.addEventListener('mouseup', this.handleSectionTitleMouseUp);

        this.mouseDragTimeout = setTimeout(() => {
            this.mouseDragTimeout = null;
            this.initDragInfo(sectionDom, pageX, pageY);
            const dragInfo: DragInfo = {
                originalDragIndex: clearanceIndex - 1,
                currentDragIndex: clearanceIndex - 1,
                outside: false,
                dragPos: this.dragPos,
                toggleDraggingSection,
                draggingArray: this.getSectionDraggingArray(event.target as HTMLElement | null),
                isSectionDrag: true,
            };
            this.savedDragInfo = { ...this.savedDragInfo, ...dragInfo, scrollTop: this.listView.scrollTop };
            this.listView.scrollTop = 0;
            toggleDraggingSection(true);
            this.registerEventListeners(true);
            this.dragDelegate.onDragStart(dragInfo);
        }, DragEventHandler.MouseDragTimeout);
    }

    private onPrepareMouseDown(event: MouseEvent): boolean {
        event.preventDefault();
        event.stopPropagation();
        if (!this.dragDelegate.draggable()) return true;
        if (event.button === 2) return true;  // 拖拽屏蔽右键
        if (this.isCtrlKey(event)) return true; // 拖拽屏蔽ctrl + 左键
        return false;
    }

    /**
     * 对初始拖拽位置信息进行初始化
     * @param cellDom
     * @param pageX
     * @param pageY
     */
    private initDragInfo(cellDom, pageX: number, pageY: number) {
        if (!this.listView) {
            this.mouseOffset = {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            };
            return;
        }

        this.listRect = this.listView.getBoundingClientRect();
        const oldScrollTop = this.listView.scrollTop;
        const topOffset = this.getTopOffset(this.listRect.top, oldScrollTop);
        let {
            // eslint-disable-next-line prefer-const
            top, right, bottom, left,
        } = cellDom?.getBoundingClientRect() ?? {};
        // eslint-disable-next-line no-param-reassign
        pageY += topOffset;
        top += topOffset;
        bottom += topOffset;
        this.mouseOffset = {  // 鼠标点击处和item四个边框的相对位置
            top: top - pageY,
            right: right - pageX,
            bottom: bottom - pageY,
            left: left - pageX,
        };
        this.dragPos = [left, top];  // 拖拽的item的left，top位置信息
        this.oldScrollTop = oldScrollTop; // 保存拖拽前的scrollTop
    }

    /**
     * 获取当前index之前与多少个元素
     * @param index
     */
    private getIndexPreCount(index: number): number {
        const selectedArray = this.dragDelegate.getSelectedArray();
        let preCount = 0;
        // 单选和多选合并为相同逻辑
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < selectedArray.length; i++) {
            const slide = selectedArray[i];
            const slideIndex = this.dataList.indexOf(slide);
            if (slideIndex !== -1 && slideIndex < index) {
                preCount += 1;
            }
        }
        return preCount;
    }

    /**
     * 获取要拖拽的元素数据列表，并拷贝拖拽的dom元素
     * @param index
     * @param cell
     */
    private getDraggingArray(index: number, cell): any[] {
        const originalDragSlide = this.dataList[index];
        const selectedArray = this.dragDelegate.getSelectedArray();
        const draggingArray = [originalDragSlide];
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < selectedArray.length; i++) {
            if (selectedArray[i] === originalDragSlide) {
                continue;
            }
            draggingArray.push(selectedArray[i]);
            if (draggingArray.length >= 3) {
                break;
            }
        }
        draggingArray.reverse();
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let j = 0; j < draggingArray.length; j++) {
            const cloneDeep = draggingArray[j] === originalDragSlide;
            draggingArray[j].cloneDom = cell?.cloneDom(cloneDeep);
        }
        return draggingArray;
    }

    private getSectionDraggingArray(target: HTMLElement | null): { cloneDom: Node }[] {
        if (!target) {
            return [];
        }
        // 有可能点到了 section-title 内的子元素，向上找到最外层
        let currentTarget = target;
        while (!currentTarget.className.includes('slide-list-section-title-selected') && currentTarget.parentElement) {
            currentTarget = currentTarget.parentElement;
        }
        const cloneDom: HTMLDivElement = currentTarget.cloneNode(true) as HTMLDivElement;
        let selectedClass: string | undefined = undefined;
        cloneDom.classList.forEach((cls) => {
            if (cls.includes('selected')) {
                selectedClass = cls;
            }
        });
        if (selectedClass) {
            cloneDom.classList.remove(selectedClass);
        }
        cloneDom.style.background = 'rgba(255, 255, 255, 0.92)';
        cloneDom.style.opacity = '0.92';
        cloneDom.style.boxShadow = '0px 0px 0px 0.62px rgba(0, 0, 0, 0.04), 0px 3.72px 19.84px 1.24px rgba(0, 0, 0, 0.16)';
        // @ts-ignore
        cloneDom.style.backdropFilter = 'blur(4.96px)';
        cloneDom.style.borderRadius = '2.48px';
        return [{ cloneDom }];
    }

    /**
     * 触发drag move事件
     * @param e
     */
    private handleMouseMove = throttle((e) => {
        if (!this.dragDelegate.draggable()) return;
        if (Object.keys(this.savedDragInfo).length === 0) return;
        // 解决拖拽时，失去当前页焦点，拖拽不释放问题
        if (e.buttons === 0) { // 表示当前鼠标没有点中
            this.handleMouseUp(e);
            return;
        }
        if (!this.listView) return;

        const { pageX, pageY } = e;
        const { topOfItem, leftOfItem, bottomOfItem, rightOfItem } = this.getDragItemRect(pageX, pageY);
        this.dragPos[0] = leftOfItem;
        this.dragPos[1] = topOfItem;

        const outside = this.isOutside(rightOfItem, leftOfItem, topOfItem, bottomOfItem);
        const slideSectionHeights = this.dragDelegate.getSlideSectionListHeights();

        let dragInfo: DragInfo;
        if (outside) {
            dragInfo = {
                outside: true,
                currentDragIndex: -1,
            };
        } else {
            const newIndex = this.getDragIndex(topOfItem, slideSectionHeights);
            const scrollAmount = this.getScrollAmount(topOfItem, bottomOfItem);
            dragInfo = {
                currentDragIndex: newIndex,
                outside: false,
                scrollAmount,
            };
            if (newIndex !== this.unfoldSectionTimeoutContext?.[0]) {
                this.unfoldSectionTimeoutContext?.[1] && clearTimeout(this.unfoldSectionTimeoutContext[1]);
                this.unfoldSectionTimeoutContext = [newIndex, setTimeout(() => {
                    if (!this.unfoldSectionTimeoutContext || Object.keys(this.savedDragInfo).length === 0) {
                        return;
                    }
                    const sectionList = this.dragDelegate.getSectionList();
                    const sectionIndex = ClearanceUtils.calcSectionIndexFromClearanceIndex(newIndex, sectionList ?? []);
                    this.dragDelegate.unfoldCorrespondingSection(newIndex);
                    this.unfoldSectionTimeoutContext = undefined;
                    if (dragInfo?.currentDragIndex && sectionList) {
                        dragInfo.currentDragIndex -= sectionList[sectionIndex].numSlides;
                        this.dragDelegate.onDragging(dragInfo);
                    }
                }, 3000)];
            }
        }
        this.savedDragInfo.currentDragIndex = dragInfo.currentDragIndex;
        this.savedDragInfo.outside = dragInfo.outside;

        this.dragDelegate.onDragging(dragInfo);
    }, 16, { trailing: false });

    private handleSectionTitleMouseMove = throttle((e: MouseEvent) => {
        if (!this.dragDelegate.draggable()) return;
        if (Object.keys(this.savedDragInfo).length === 0) return;
        // 解决拖拽时，失去当前页焦点，拖拽不释放问题
        if (e.buttons === 0) { // 表示当前鼠标没有点中
            this.handleMouseUp(e);
            return;
        }
        if (!this.listView) return;

        const { pageX, pageY } = e;
        const { topOfItem, leftOfItem, bottomOfItem, rightOfItem } = this.getDragItemRect(pageX, pageY);
        this.dragPos[0] = leftOfItem;
        this.dragPos[1] = topOfItem;

        const outside = this.isOutside(rightOfItem, leftOfItem, topOfItem, bottomOfItem);
        const slideSectionHeights = this.dragDelegate.getSectionListHeights();

        // let dragInfo: DragInfo;
        const newIndex = this.getSectionDragIndex(topOfItem, slideSectionHeights);
        const scrollAmount = this.getScrollAmount(topOfItem, bottomOfItem);
        const dragInfo: DragInfo = {
            isSectionDrag: true,
            currentDragIndex: newIndex,
            outside,
            scrollAmount,
        };
        this.savedDragInfo.currentDragIndex = dragInfo.currentDragIndex;
        this.savedDragInfo.outside = dragInfo.outside;

        this.dragDelegate.onDragging(dragInfo);
    }, 16, { trailing: false });

    private isOutside(rightOfSlide, leftOfSlide, topOfSlide, bottomOfSlide) {
        const { listRect } = this;
        return rightOfSlide < listRect.left || leftOfSlide > listRect.right
            || topOfSlide > listRect.bottom || bottomOfSlide < listRect.top;
    }

    private getDragIndex(topOfSlide, slideSectionHeights: [THeight, TClearance][]) {
        const effectiveTop = this.getEffectiveTop(topOfSlide);

        if (effectiveTop >= slideSectionHeights.reduce((acc, cur) => acc + cur[0], 0)) {
            let [, lastClearanceIndex] = slideSectionHeights[slideSectionHeights.length - 1];
            if (!this.dragDelegate.hasSections()) {
                // 没有节的情况下，最后一张幻灯片的最后还会多一个clearanceIndex，因此要加一
                lastClearanceIndex += 1;
            }
            return lastClearanceIndex;
        }
        let index = -1;
        let accumulatedHeight = this.getAccumulatedHeight();
        slideSectionHeights.forEach(([height, clearanceIndex]) => {
            if (index !== -1) {
                return;
            }
            if (effectiveTop > accumulatedHeight && effectiveTop < accumulatedHeight + height) {
                index = clearanceIndex;
                return;
            }
            accumulatedHeight += height;
        });
        return index;
    }

    /**
     * 获取初始高度
     */
    private getAccumulatedHeight(): number {
        // 选中的是版式时，相对于普通编辑模式；初始高度是一个母版的高度，减去一个间隙的高度
        // 此高度是一个大约值，暂不考虑精确计算
        if (this.getSelectionService()?.currentSlideType === UIModelType.LAYOUT) {
            return this.dragDelegate.getCellHeight() - DEFAULT_CLEARANCE_HEIGHT;
        }
        return 0;
    }

    private getSectionDragIndex(topOfSlide: number, sectionHeights: number[]): number {
        if (sectionHeights.length <= 0) {
            return -1;
        }
        const effectiveTop = this.getEffectiveTop(topOfSlide);
        if (effectiveTop <= 0) {
            return -1;
        }
        if (effectiveTop > sectionHeights.reduce((acc, cur) => acc + cur, 0)) {
            return sectionHeights.length - 1;
        }
        let index = -1;
        let accumulatedHeight = 0;
        sectionHeights.forEach((height, i) => {
            if (index !== -1) {
                return;
            }
            if (effectiveTop > accumulatedHeight && effectiveTop <= accumulatedHeight + height) {
                index = i;
                return;
            }
            accumulatedHeight += height;
        });
        if (index >= sectionHeights.length) {
            index = sectionHeights.length;
        }
        return index;
    }

    private getEffectiveTop(topOfSlide: number): number {
        const { listRect } = this;
        const newScrollTop = this.listView?.scrollTop || 0;
        const scrollOffset = newScrollTop - this.oldScrollTop;
        const scrollTop = this.oldScrollTop + scrollOffset;
        const offset = this.dragDelegate.hasSections() ? 0 : this.dragDelegate.getCellHeight() / 2;
        return topOfSlide - listRect.top + scrollTop + offset;
    }

    private getDragItemRect(pageX, pageY) {
        const topOffset = 0;
        // eslint-disable-next-line no-param-reassign
        pageY += topOffset;
        const { mouseOffset } = this;
        const topOfItem = pageY + (mouseOffset?.top ?? 0);
        const bottomOfItem = pageY + (mouseOffset?.bottom ?? 0);
        const leftOfItem = pageX + (mouseOffset?.left ?? 0);
        const rightOfItem = pageX + (mouseOffset?.right ?? 0);

        return { topOfItem, leftOfItem, bottomOfItem, rightOfItem };
    }

    private getScrollAmount(topOfSlide, bottomOfSlide) {
        const { top, height } = this.listRect;
        const topScroll = top + SCROLL_AREA;
        const bottomScroll = top + height - SCROLL_AREA;
        let scrollAmount;
        if (topOfSlide < topScroll) {
            scrollAmount = -5;
        } else if (bottomOfSlide > bottomScroll) {
            scrollAmount = 5;
        } else {
            scrollAmount = null;
        }
        return scrollAmount;
    }

    /**
     * 触发mouse up事件
     */
    private handleMouseUp = (event: MouseEvent) => {
        if (this.mouseDragTimeout) {
            clearTimeout(this.mouseDragTimeout);
            document.removeEventListener('mouseup', this.handleMouseUp);

            this.mouseDragTimeout = null;
            this.savedDragInfo = {};
            return;
        }
        this.unRegisterListener();

        const { topOfItem } = this.getDragItemRect(event.pageX, event.pageY);
        const finalIndex = this.getDragIndex(topOfItem, this.dragDelegate.getSlideSectionListHeights());

        const dragInfo: DragInfo = {
            originalDragItem: null,
            currentDragIndex: -1,
            originalDragIndex: this.savedDragInfo.originalDragIndex,
            outside: false,
            finalIndex,
        };
        this.dragDelegate.onDragEnd(dragInfo);
        this.clearDragData();
    };

    private handleSectionTitleMouseUp = (event: MouseEvent) => {
        if (this.mouseDragTimeout) {
            clearTimeout(this.mouseDragTimeout);
            document.removeEventListener('mouseup', this.handleSectionTitleMouseUp);

            this.savedDragInfo = {};
            this.mouseDragTimeout = null;
            return;
        }
        this.savedDragInfo.toggleDraggingSection?.(false);
        this.listView.scrollTop = this.savedDragInfo.scrollAmount;
        const { topOfItem, leftOfItem, bottomOfItem, rightOfItem } = this.getDragItemRect(event.pageX, event.pageY);
        const finalIndex = this.getSectionDragIndex(topOfItem, this.dragDelegate.getSectionListHeights());
        this.unRegisterListener(true);
        const outside = this.isOutside(rightOfItem, leftOfItem, topOfItem, bottomOfItem);
        const dragInfo: DragInfo = {
            originalDragItem: null,
            currentDragIndex: -1,
            originalDragIndex: this.savedDragInfo.originalDragIndex ?? -1,
            outside,
            finalIndex,
            isSectionDrag: true,
            originalSectionIndex: this.savedDragInfo.originalSectionIndex,
        };
        this.dragDelegate.onDragEnd(dragInfo);
    };

    private clearDragData() {
        this.mouseOffset = null;
        this.oldScrollTop = 0;
        this.savedDragInfo = {};
        this.dragPos = [0, 0];
    }

    private registerEventListeners(isSectionTitle?: boolean) {
        const handleMouseUp = isSectionTitle ? this.handleSectionTitleMouseUp : this.handleMouseUp;
        const handleMouseMove = isSectionTitle ? this.handleSectionTitleMouseMove : this.handleMouseMove;
        // 主页面开始监听
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    }

    private unRegisterListener(isSectionTitle?: boolean) {
        const handleMouseUp = isSectionTitle ? this.handleSectionTitleMouseUp : this.handleMouseUp;
        const handleMouseMove = isSectionTitle ? this.handleSectionTitleMouseMove : this.handleMouseMove;
        document.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    }

    private getTopOffset(listTop, scrollTop) {
        return 0;
    }

    private get listView() {
        return this.dragDelegate.getListView();
    }

    private get dataList() {
        return this.dragDelegate.getDataList();
    }

    private isCtrlKey(ev) {
        return ev.metaKey || ev.ctrlKey;
    }

    private getSelectionService(): ISelectionService | undefined {
        return this.dragDelegate.getServices().selectionService;
    }
}
